import {
    Dialog,
    Flash,
    Form,
    format,
    Instance,
    Objekt,
    Promize,
    Table,
} from '@siposdani87/sui-js';
import saveAs from 'file-saver';
import { app } from '../app';
import { AssetService } from '@services/assetService';
import { LanguageService } from '@services/languageService';
import { OrganizationService } from '@services/organizationService';
import { OrganizationUserService } from '@services/organizationUserService';
import { initTable } from '@utils/collectionSetup';
import { DashboardController } from './dashboardController';

export class OrganizationUsersIndexController extends DashboardController {
    static readonly inject = [
        'instances',
        'organizationUserService',
        'organizationService',
        'assetService',
        'languageService',
    ] as const;

    organizationId!: string;
    organizationUsersTable!: Table;
    private dialog: Dialog;
    private flash: Flash;

    constructor(
        instances: Instance,
        private organizationUserService: OrganizationUserService,
        private organizationService: OrganizationService,
        private assetService: AssetService,
        private languageService: LanguageService,
    ) {
        super(instances);

        this.dialog = instances.dialog;
        this.flash = instances.flash;
    }

    protected override _initLayout(): void {
        this.organizationId = this.state.getParam('organizationId');

        this._initToolbarButtons();
        this._initFilterForm();

        this._drawContent();
    }

    protected override _drawContent(): Promize | boolean {
        this._initOrganizationUserTable();
        return true;
    }

    private _initOrganizationUserTable(): void {
        this.organizationUsersTable = initTable(
            this.organizationUsersTable,
            this.dom,
            '.organization-users-table',
            this.organizationUserService,
            {
                entityKey: 'organization_users',
                fetchData: (params) =>
                    this.organizationUserService.getAllByOrganization(
                        this.organizationId,
                        params,
                    ),
                extraParams: (params) => params.set('query', this.query),
            },
            [
                {
                    style: () => {
                        return [
                            'edit',
                            this.languageService.translate('buttons.edit'),
                        ];
                    },
                    click: (organizationUser) => {
                        this.organizationUserService
                            .editDialog(organizationUser)
                            .then(() => {
                                this._drawContent();
                            });
                    },
                },
            ],
        )!;
    }

    private _initToolbarButtons(): void {
        this.helper.iconButton('.btn-organizations-show', this.dom, () => {
            this.state.goBack('organizations.show', {
                organizationId: this.organizationId,
            });
        });

        this.helper.fabButton('.btn-invite', this.dom, () => {
            this._showOrganizationUsersInviteDialog();
        });

        this.helper.fabButton('.btn-join-manual', this.dom, () => {
            this._downloadJoinManual();
        });
    }

    private _downloadJoinManual(): void {
        this.organizationService
            .downloadJoinManual(this.organizationId)
            .then((response, filename) => {
                saveAs(response.get<Blob>('raw'), filename);
            });
    }

    private _showOrganizationUsersInviteDialog(): void {
        this.dialog
            .loadTemplate(
                format('/client/v1/organizations/{0}/invite.html', [
                    this.organizationId,
                ]),
            )
            .then((dialogKnot) => {
                const form = new Form(dialogKnot);
                form.on('submit', (formData: Objekt) => {
                    form.lock();
                    const invitationData = formData.get<Objekt>('invitation');
                    this.organizationService
                        .invite(this.organizationId, invitationData)
                        .then(
                            (response) => {
                                this.flash.addMessage(response.get('message'));
                                this.dialog.close();
                            },
                            (response) => {
                                this.flash.addMessage(response.get('message'));
                                form.unlock();
                                form.setErrors(response.get<Objekt>('errors'));
                            },
                        );
                });
                form.on('reset', () => {
                    this.dialog.close();
                });
                this.dialog.open();
            });
    }
}

export const organizationUsersIndexController = app.controller(
    'organizationUsersIndexController',
    OrganizationUsersIndexController,
);
